import React, { lazy } from 'react';
import { motion } from 'framer-motion';
import { CheckIcon, HeadphonesIcon, DownloadIcon, StarIcon, ArrowRightIcon } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
const MAIN_PRODUCT_URL = 'https://www.mentefluir.com/cart/44741027889199:1';
const ORDER_BUMP_URL = 'https://www.mentefluir.com/cart/44741027889199:1,44977860968495:1';
interface PurchaseSectionProps {
  orderBumpChecked?: boolean;
}
export function PurchaseSection({
  orderBumpChecked = false
}: PurchaseSectionProps) {
  const {
    t
  } = useLanguage();
  const handlePurchase = () => {
    window.location.href = orderBumpChecked ? ORDER_BUMP_URL : MAIN_PRODUCT_URL;
  };
  return <motion.section initial={{
    opacity: 0
  }} whileInView={{
    opacity: 1
  }} viewport={{
    once: true,
    margin: '-20px',
    amount: 0.2
  }} transition={{
    duration: 0.5,
    ease: 'easeOut'
  }} className="my-12 md:my-20 py-10 md:py-16 px-5 md:px-6 bg-gradient-to-br from-[#2C4A3E] to-[#1F3329] rounded-2xl border-[3px] border-[#D4A574] shadow-xl" data-id="element-118">
      <div className="max-w-5xl mx-auto" data-id="element-119">
        <div className="flex flex-col items-center mb-4 md:mb-6" data-id="element-120">
          <div className="flex items-center gap-2 text-[#D4A574] mb-2" data-id="element-121">
            <DownloadIcon className="w-4 h-4 md:w-5 md:h-5" data-id="element-122" />
            <span className="text-sm md:text-base font-medium tracking-wide uppercase" data-id="element-123">
              Edición Digital
            </span>
          </div>
          <div className="flex gap-1" data-id="element-124">
            {[...Array(5)].map((_, i) => <StarIcon key={i} className="w-4 h-4 md:w-5 md:h-5 fill-[#D4A574] text-[#D4A574]" data-id="element-125" />)}
          </div>
        </div>
        <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl text-[#FBF8F3] text-center mb-8 md:mb-12" data-id="element-126">
          {t.purchase.title}
        </h2>

        <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-center" data-id="element-127">
          {/* Devices Image */}
          <div data-id="element-128">
            <img src="https://cdn.magicpatterns.com/uploads/hvv7psoS96Yn7KSvWvHZJz/Dispositivos_mostrando_e-book_sobre_sanacion_financiera.webp" alt="E-book on devices" className="w-full rounded-lg shadow-2xl" loading="lazy" decoding="async" data-id="element-129" />
          </div>

          {/* Purchase Details */}
          <div className="space-y-6 md:space-y-8" data-id="element-130">
            {/* Price Badge */}
            <div className="text-center md:text-left" data-id="element-131">
              <p className="text-[#FBF8F3]/70 text-base md:text-lg mb-2 font-medium" data-id="element-132">
                ANTES <span className="line-through" data-id="element-133">{'$32'}</span>
              </p>
              <div className="inline-block bg-gradient-to-br from-[#E8B86D] via-[#D4A574] to-[#C87941] rounded-2xl px-6 md:px-8 py-3 md:py-4 mb-4 md:mb-6" data-id="element-134">
                <div className="flex items-baseline gap-2" data-id="element-135">
                  <span className="text-4xl md:text-5xl font-bold text-[#2C4A3E]" data-id="element-136">
                    {t.purchase.price}
                  </span>
                  <span className="text-lg md:text-xl text-[#2C4A3E]" data-id="element-137">
                    {t.purchase.priceLabel}
                  </span>
                </div>
              </div>
            </div>

            {/* Features */}
            <div data-id="element-138">
              <p className="text-center text-xs md:text-sm font-semibold text-[#D4A574] uppercase tracking-widest mb-2" data-id="element-139">
                Total incluye
              </p>
              <ul className="space-y-3 md:space-y-4" data-id="element-140">
                {(() => {
                const guarantee = 'Garantía incondicional de 7 días';
                // Find the audio item index
                const audioIndex = t.purchase.features.findIndex(f => f.toLowerCase().includes('audio') || f.toLowerCase().includes('reprogramación') || f.toLowerCase().includes('reprogramming'));
                // Insert guarantee before the audio item
                const items = [...t.purchase.features];
                if (audioIndex !== -1) {
                  items.splice(audioIndex, 0, guarantee);
                } else {
                  items.push(guarantee);
                }
                return items.map((feature, index) => {
                  const isAudio = feature.toLowerCase().includes('audio') || feature.toLowerCase().includes('reprogramación') || feature.toLowerCase().includes('reprogramming');
                  return <li key={index} className="flex items-start gap-3 text-[#FBF8F3]" data-id="element-141">
                        {isAudio ? <HeadphonesIcon className="w-5 h-5 md:w-6 md:h-6 text-[#D4A574] flex-shrink-0 mt-1" data-id="element-142" /> : <CheckIcon className="w-5 h-5 md:w-6 md:h-6 text-[#D4A574] flex-shrink-0 mt-1" data-id="element-143" />}
                        <span className="text-base md:text-lg" data-id="element-144">{feature}</span>
                      </li>;
                });
              })()}
              </ul>
            </div>

            {/* Bonus Cards */}
            <div className="space-y-3 md:space-y-4" data-id="element-145">
              {/* Bonus 1 */}
              <div className="rounded-xl border border-[#D4A574]/40 bg-[#1E3530]/60 overflow-hidden" data-id="element-146">
                <div className="flex items-center justify-center gap-2 bg-[#D4A574]/20 py-2 px-4 border-b border-[#D4A574]/30" data-id="element-147">
                  <span className="text-lg" data-id="element-148">🎁</span>
                  <span className="text-xs md:text-sm font-bold text-[#E8B86D] tracking-widest uppercase" data-id="element-149">
                    Bono Gratis
                  </span>
                </div>
                <div className="flex gap-3 p-3 md:p-4 items-center" data-id="element-150">
                  <img src="https://cdn.magicpatterns.com/uploads/u1YFVzyGvpT9KMdm94mZTU/ChatGPT_Image_21_feb_2026,_21_52_22.webp" alt="Workbook de Integración" className="w-36 md:w-44 rounded-lg shadow-lg flex-shrink-0" data-id="element-151" />
                  <div className="flex-1 min-w-0" data-id="element-152">
                    <p className="font-serif text-sm md:text-base text-[#E8D5B5] font-bold leading-tight mb-1" data-id="element-153">
                      Workbook de Integración{' '}
                      <span className="text-[#D4A574]" data-id="element-154">(20 páginas)</span>
                    </p>
                    <p className="text-xs md:text-sm text-[#D4C4A8] leading-snug mb-2" data-id="element-155">
                      Acción con paso a paso
                    </p>
                    <p className="text-xs md:text-sm text-[#FBF8F3]/70" data-id="element-156">
                      Valor{' '}
                      <span className="line-through text-[#FBF8F3]/50" data-id="element-157">
                        $19
                      </span>{' '}
                      <span className="text-[#E8B86D] font-semibold" data-id="element-158">
                        Hoy lo obtienes gratis
                      </span>
                    </p>
                  </div>
                </div>
              </div>

              {/* Bonus 2 */}
              <div className="rounded-xl border border-[#D4A574]/40 bg-[#1E3530]/60 overflow-hidden" data-id="element-159">
                <div className="flex items-center justify-center gap-2 bg-[#D4A574]/20 py-2 px-4 border-b border-[#D4A574]/30" data-id="element-160">
                  <span className="text-lg" data-id="element-161">🎁</span>
                  <span className="text-xs md:text-sm font-bold text-[#E8B86D] tracking-widest uppercase" data-id="element-162">
                    Bono Gratis
                  </span>
                </div>
                <div className="flex gap-3 p-3 md:p-4 items-center" data-id="element-163">
                  <img src="https://cdn.magicpatterns.com/uploads/dB2hFZd8B8Rc8fRZwNDFsH/ChatGPT_Image_21_feb_2026,_21_56_47.webp" alt="Mini guía para duplicar ingresos sin culpa" className="w-36 md:w-44 rounded-lg shadow-lg flex-shrink-0" data-id="element-164" />
                  <div className="flex-1 min-w-0" data-id="element-165">
                    <p className="font-serif text-sm md:text-base text-[#E8D5B5] font-bold leading-tight mb-1" data-id="element-166">
                      Mini guía para duplicar ingresos sin culpa{' '}
                      <span className="text-[#D4A574]" data-id="element-167">(20 páginas)</span>
                    </p>
                    <p className="text-xs md:text-sm text-[#D4C4A8] leading-snug mb-2" data-id="element-168">
                      Aprende a escalar ingresos sólo con creer que te lo
                      mereces
                    </p>
                    <p className="text-xs md:text-sm text-[#FBF8F3]/70" data-id="element-169">
                      Valor{' '}
                      <span className="line-through text-[#FBF8F3]/50" data-id="element-170">
                        $30
                      </span>{' '}
                      <span className="text-[#E8B86D] font-semibold" data-id="element-171">
                        Hoy lo obtienes gratis
                      </span>
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* CTA Button */}
            <motion.button onClick={handlePurchase} whileHover={{
            scale: 1.05
          }} whileTap={{
            scale: 0.98
          }} className="block w-full py-2.5 md:py-3 rounded-xl text-lg md:text-xl font-bold transition-all duration-300 text-center cursor-pointer text-white relative overflow-hidden" style={{
            background: '#E4574F',
            border: '1px solid rgba(255, 140, 130, 0.5)'
          }} data-id="element-172">
              <span className="relative z-10 flex flex-col items-center justify-center" data-id="element-173">
                <span className="inline-flex items-center gap-2 text-lg md:text-xl font-bold" data-id="element-174">
                  Acceder ahora
                  <ArrowRightIcon className="w-5 h-5 md:w-6 md:h-6" data-id="element-175" />
                </span>
                <span className="text-[10px] md:text-xs font-semibold tracking-[0.2em] uppercase text-white/80 mt-0.5" data-id="element-176">
                  🔒 Pago seguro con tarjeta
                </span>
              </span>
            </motion.button>

            {/* Trust Indicators */}
            <div className="flex items-center justify-center gap-4 md:gap-6 text-xs md:text-sm text-[#FBF8F3]/80" data-id="element-177">
              <span data-id="element-178">
                🔒 Pago vía PayPal. Acepta tarjeta de débito o crédito. No
                necesitas cuenta.
              </span>
              <span data-id="element-179">⚡ Acceso inmediato al pagar.</span>
            </div>
          </div>
        </div>
      </div>
    </motion.section>;
}